import { useRef } from "react";

/**
 * Icon-only button that prints a 4-line ZPL address label via the browser
 * print dialog (raw ZPL text in a hidden iframe -> contentWindow.print()).
 * The OS print queue must be a Zebra/ZPL-aware "raw" printer for the output
 * to come out as a real label — same technique as PurchaseZplButton.jsx.
 *
 * `lines` = [customer, contact, address, cityStateZip] (exactly 4 strings).
 */
export default function ZplAddressLabelButton({ lines, title = "Print label" }) {
  const iframeRef = useRef(null);

  const generateZplCode = () => {
    const labelWidth = 812; // 4" at 203 DPI
    const labelHeight = 406; // 2" at 203 DPI
    const fontSize = { height: 46, width: 38 };
    const rowPositions = [30, 120, 210, 300];
    const xPosition = 30;

    const rowsToUse = [...(lines || [])].slice(0, 4);
    while (rowsToUse.length < 4) rowsToUse.push("");

    const zplElements = rowsToUse
      .map(
        (value, i) =>
          `^FO${xPosition},${rowPositions[i]}^A0,${fontSize.height},${fontSize.width}^FD${value || ""}^FS`,
      )
      .join("\n      ");

    return `
      ^XA
      ^PW${labelWidth}^LL${labelHeight}
      ${zplElements}
      ^XZ
    `;
  };

  const printLabel = () => {
    if (!lines || lines.every((l) => !l)) return;

    const iframe = iframeRef.current;
    const doc = iframe?.contentWindow;
    if (!doc?.document) return;

    doc.document.open();
    doc.document.write(`<html><body><pre>${generateZplCode()}</pre></body></html>`);
    doc.document.close();
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-link p-0 border-0 lh-1"
        title={title}
        disabled={!lines || lines.every((l) => !l)}
        onClick={printLabel}
      >
        <i className="mdi mdi-printer"></i>
      </button>
      <iframe ref={iframeRef} style={{ display: "none" }} title="ZPL Print Frame"></iframe>
    </>
  );
}
